<template>
  <div class="flow-container" ref="container">
    <svg 
      viewBox="0 -10 900 350" 
      preserveAspectRatio="xMidYMid meet" 
      class="flow-svg"
    >
      <defs>
        <!-- Watery Droplet Gradient -->
        <radialGradient id="waterSphere" cx="30%" cy="30%" r="70%">
          <stop offset="0%" stop-color="#ffffff" stop-opacity="0.9" />
          <stop offset="100%" stop-color="#668cf6" stop-opacity="1" />
        </radialGradient>

        <!-- Glow Filter for Shimmer -->
        <filter id="glow" x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur stdDeviation="3" result="coloredBlur" />
          <feMerge>
            <feMergeNode in="coloredBlur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>

      <!-- PV Channel -->
      <path :class="{ 'is-hidden': !deviceSource.includes('pv'), 'path-no-data': pvPower === null }" d="M160 83 H262 Q277 83 277 98 V138 Q277 153 292 153 H405" class="bus-line" />

      <!-- Battery Channel -->
      <path :class="{ 'is-hidden': !deviceSource.includes('battery'), 'path-no-data': batteryPower === null }" d="M160 267 H262 Q277 267 277 252 V212 Q277 197 292 197 H405" class="bus-line" />

      <!-- Load Channel -->
      <path :class="{ 'is-hidden': !deviceSource.includes('ac_load'), 'path-no-data': loadCurrent === null }" d="M505 153 H608 Q623 153 623 138 V98 Q623 83 638 83 H740" class="bus-line" />

      <!-- Grid Channel -->
      <path :class="{ 'is-hidden': !deviceSource.includes('grid'), 'path-no-data': gridPower === null }" d="M505 197 H608 Q623 197 623 212 V252 Q623 267 638 267 H740" class="bus-line" />

      <!-- PV (Top Left) -->
      <image :class="{ 'is-hidden': !deviceSource.includes('pv'), 'img-device': true }" :href="assets.pv" x="50" y="40" width="80" height="70" />
      <text :class="{ 'is-hidden': !deviceSource.includes('pv') }" x="150" y="45" class="label-text" text-anchor="start">PV Load</text>
      <text :class="{ 'is-hidden': !deviceSource.includes('pv'), 'text-no-data': pvPower === null }" x="150" y="75" class="value-text" text-anchor="start">{{ pvValue }}</text>

      <!-- Battery (Bottom Left) -->
      <image :class="{ 'is-hidden': !deviceSource.includes('battery'), 'img-device': true }" :href="assets.battery" x="60" y="230" width="65" height="90" />
      <text :class="{ 'is-hidden': !deviceSource.includes('battery') }" x="150" y="225" class="label-text" text-anchor="start">Battery Load</text>
      <text :class="{ 'is-hidden': !deviceSource.includes('battery'), 'text-no-data': batteryPower === null }" x="150" y="255" class="value-text" text-anchor="start">{{ batteryValue }}</text>

      <!-- Inverter (Center) -->
      <image :href="assets.inverter" x="420" y="135" width="70" height="70" />

      <!-- Load (Top Right) -->
      <image :class="{ 'is-hidden': !deviceSource.includes('ac_load'), 'img-device': true }" :href="assets.load" x="760" y="40" width="70" height="70" />
      <text :class="{ 'is-hidden': !deviceSource.includes('ac_load') }" x="750" y="45" class="label-text" text-anchor="end">AC Load</text>
      <text :class="{ 'is-hidden': !deviceSource.includes('ac_load'), 'text-no-data': loadCurrent === null }" x="750" y="75" class="value-text" text-anchor="end">{{ loadValue }}</text>

      <!-- Grid (Bottom Right) -->
      <image :class="{ 'is-hidden': !deviceSource.includes('grid'), 'img-device': true }" :href="assets.grid" x="760" y="230" width="80" height="80" />
      <text :class="{ 'is-hidden': !deviceSource.includes('grid') }" x="750" y="225" class="label-text" text-anchor="end">Grid Load</text>
      <text :class="{ 'is-hidden': !deviceSource.includes('grid'), 'text-no-data': gridPower === null }" x="750" y="255" class="value-text" text-anchor="end">{{ gridValue }}</text>

      <!-- PV Flow -->
      <path :class="{ 'is-hidden': !deviceSource.includes('pv') || !hasPvPower }" d="M160 83 H262 Q277 83 277 98 V138 Q277 153 292 153 H405" 
            stroke="#668cf6" stroke-width="12" fill="none" stroke-linecap="round" filter="url(#glow)"
            stroke-dasharray="20 1000" stroke-dashoffset="50">
        <animate attributeName="stroke-dasharray" values="20 1000; 50 1000; 20 1000" dur="2s" repeatCount="indefinite" />
        <animate attributeName="stroke-dashoffset" from="50" to="-500" dur="6s" repeatCount="indefinite" />
      </path>

      <!-- Battery Flow -->
      <path :class="{ 'is-hidden': batteryFlowClass !== 'flow-forward' || !deviceSource.includes('battery') }" d="M405 197 H292 Q277 197 277 212 V252 Q277 267 262 267 H140" 
            stroke="#668cf6" stroke-width="12" fill="none" stroke-linecap="round" filter="url(#glow)"
            stroke-dasharray="20 1000" stroke-dashoffset="50">
        <animate attributeName="stroke-dasharray" values="20 1000; 50 1000; 20 1000" dur="2s" repeatCount="indefinite" />
        <animate attributeName="stroke-dashoffset" from="50" to="-500" dur="6s" repeatCount="indefinite" />
      </path>
      <path :class="{ 'is-hidden': batteryFlowClass !== 'flow-backward' }" d="M160 267 H262 Q277 267 277 252 V212 Q277 197 292 197 H405" 
            stroke="#668cf6" stroke-width="12" fill="none" stroke-linecap="round" filter="url(#glow)"
            stroke-dasharray="20 1000" stroke-dashoffset="50">
        <animate attributeName="stroke-dasharray" values="20 1000; 50 1000; 20 1000" dur="2s" repeatCount="indefinite" />
        <animate attributeName="stroke-dashoffset" from="50" to="-500" dur="6s" repeatCount="indefinite" />
      </path>

      <!-- Load Flow -->
      <path :class="{ 'is-hidden': !deviceSource.includes('ac_load') || !hasLoadCurrent }" d="M505 153 H608 Q623 153 623 138 V98 Q623 83 638 83 H740" 
            stroke="#668cf6" stroke-width="12" fill="none" stroke-linecap="round" filter="url(#glow)"
            stroke-dasharray="20 1000" stroke-dashoffset="50" opacity="0">
        <set attributeName="opacity" to="1" begin="3s" fill="freeze" />
        <animate attributeName="stroke-dasharray" values="20 1000; 50 1000; 20 1000" dur="2s" begin="3s" repeatCount="indefinite" />
        <animate attributeName="stroke-dashoffset" from="50" to="-500" dur="6s" begin="3s" repeatCount="indefinite" />
      </path>

      <!-- Grid Flow -->
      <path :class="{ 'is-hidden': gridFlowClass !== 'flow-forward' || !deviceSource.includes('grid') }" d="M505 197 H608 Q623 197 623 212 V252 Q623 267 638 267 H740"
            stroke="#668cf6" stroke-width="12" fill="none" stroke-linecap="round" filter="url(#glow)"
            stroke-dasharray="20 1000" stroke-dashoffset="50" opacity="0">
        <set attributeName="opacity" to="1" begin="3s" fill="freeze" />
        <animate attributeName="stroke-dasharray" values="20 1000; 50 1000; 20 1000" dur="2s" begin="3s" repeatCount="indefinite" />
        <animate attributeName="stroke-dashoffset" from="50" to="-500" dur="6s" begin="3s" repeatCount="indefinite" />
      </path>
      <path :class="{ 'is-hidden': gridFlowClass !== 'flow-backward' || !deviceSource.includes('grid') }" d="M740 267 H638 Q623 267 623 252 V212 Q623 197 608 197 H505"
            stroke="#668cf6" stroke-width="12" fill="none" stroke-linecap="round" filter="url(#glow)"
            stroke-dasharray="20 1000" stroke-dashoffset="50">
        <animate attributeName="stroke-dasharray" values="20 1000; 50 1000; 20 1000" dur="2s" repeatCount="indefinite" />
        <animate attributeName="stroke-dashoffset" from="50" to="-500" dur="6s" repeatCount="indefinite" />
      </path>
    </svg>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import BatteryService from "@/services/Battery";

export default {
  name: "FlowDiagram",
  data() {
    return {
      pvPower: 0,
      gridPower: 0,
      batteryPower: 0,
      loadCurrent: 0,
      interval: null,
      loading: false,
      assets: {
        pv: require('@/assets/flow_diagram/rooftop.svg'),
        battery: require('@/assets/flow_diagram/battery.svg'),
        inverter: require('@/assets/flow_diagram/inverter.svg'),
        load: require('@/assets/flow_diagram/houseEnergyUsed.svg'),
        grid: require('@/assets/flow_diagram/grid.svg')
      }
    };
  },
  computed: {
    ...mapGetters(["getSelectedPlantId", "getRules"]),
    deviceSource() {
      const accessConfig = this.getRules(["rule", "access_config"]);
      
      return accessConfig.battery && accessConfig.battery.power_flow && accessConfig.battery.power_flow.source ? accessConfig.battery.power_flow.source : [];
    },
    pvValue() {
      return this.pvPower !== null ? `${this.pvPower} kW` : "N/A";
    },
    gridValue() {
      return this.gridPower !== null ? `${Math.abs(this.gridPower)} kW` : "N/A";
    },
    batteryValue() {
      return this.batteryPower !== null ? `${Math.abs(this.batteryPower)} kW` : "N/A";
    },
    loadValue() {
      return this.loadCurrent !== null ? `${this.loadCurrent} A` : "N/A";
    },
    hasPvPower() {
      return this.pvPower > 0;
    },
    hasLoadCurrent() {
      return this.loadCurrent > 0;
    },
    hasBatteryPower() {
      return this.batteryPower !== 0 && this.batteryPower !== null;
    },
    hasGridPower() {
      return this.gridPower !== 0 && this.gridPower !== null;
    },
    batteryFlowClass() {
      if (this.batteryPower > 0) return 'flow-backward';
      if (this.batteryPower < 0) return 'flow-forward';
      return '';
    },
    gridFlowClass() {
     if (this.gridPower > 0) return 'flow-backward';
     if (this.gridPower < 0) return 'flow-forward';
     return '';
    }
  },
  mounted() {
    this.startPolling();
  },
  beforeDestroy() {
    this.stopPolling();
  },
  methods: {
    startPolling() {
      this.fetchData();
      if (this.interval) clearInterval(this.interval);
      this.interval = setInterval(this.fetchData, 60000);
    },
    stopPolling() {
      if (this.interval) clearInterval(this.interval);
      this.interval = null;
    },
    async fetchData() {
      if (!this.getSelectedPlantId) return;
      this.loading = true;
      try {
        const response = await BatteryService.fetchFlowDiagramData({
          plantId: this.getSelectedPlantId, 
          deviceSourceList: this.deviceSource 
        });
        const msg = response.data.message;
        this.pvPower = msg.pv;
        this.gridPower = msg.grid;
        this.batteryPower = msg.battery;
        this.loadCurrent = msg.ac_load;
      } catch (err) {
        console.error("Error fetching flow diagram data", err);
      } finally {
        this.loading = false;
      }
    }
  },
  watch: {
    getSelectedPlantId: {
      handler() {
        this.startPolling();
      }
    },
    deviceSource: {
      handler() {
        this.fetchData();
      }
    }
  }
};
</script>

<style scoped>
.flow-container {
  width: 100%;
  height: 100%;
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
}

.flow-svg {
  width: 100%;
  height: 100%;
  max-width: 100%; 
  max-height: 100%; 
}

.bus-line {
  stroke: var(--v-primaryText-base);
  stroke-width: 1.5;
  fill: none;
  opacity: 0.5;
}

.label-text {
  fill: var(--v-primaryText-base);
  font-size: 16px;
  font-family: 'Roboto', sans-serif;
  opacity: 0.9;
}

.value-text {
  fill: #668cf6;
  font-weight: bold;
  font-size: 22px;
  font-family: 'Roboto', sans-serif;
}

.theme--dark .img-device {
  filter: brightness(0) saturate(100%) invert(78%) sepia(0%) saturate(26%) hue-rotate(258deg) brightness(98%) contrast(98%);
}

.theme--light .img-device {
  filter: brightness(0) saturate(100%) invert(52%) sepia(16%) saturate(341%) hue-rotate(181deg) brightness(98%) contrast(87%);
}

@media (max-width: 600px) {
  .label-text {
    font-size: 14px;
  }
  .value-text {
    font-size: 16px;
  }
}

.anim-flow, .flow-forward, .flow-backward {
  stroke: #FBBC05;
  stroke-dasharray: 10;
  animation: flowAnimation 1s linear infinite;
}

.flow-backward {
  animation-direction: reverse;
}

.is-hidden {
  visibility: hidden;
  opacity: 0;
  transition: opacity 0.3s;
}

@keyframes flowAnimation {
  from {
    stroke-dashoffset: 20;
  }
  to {
    stroke-dashoffset: 0;
  }
}

.path-no-data {
  stroke: #ff4d4d !important;
  opacity: 1 !important;
}

.text-no-data {
  fill: #ff4d4d !important;
}
</style>
